import { useEffect, useMemo, useState } from 'react';
import { flowCadence } from '../lib/aggregate.js';
import { formatAbsoluteTime, formatLedgerTime, formatNumber, formatRelativeTime, shortAddress } from '../lib/format.js';

const flowFilters = [
  ['all', 'All'],
  ['rwa', 'RWA'],
  ['stablecoin', 'Stable'],
  ['transfer', 'Transfers'],
  ['issuance', 'Mint / redeem'],
  ['dex', 'DEX'],
  ['trust', 'Trust'],
  ['program', 'Program'],
];

const sourceFilters = [
  ['all', 'All sources'],
  ['live', 'Live'],
  ['history', 'History'],
];

const CADENCE_MINUTES = 30;

function flowGroup(flow) {
  if (flow.transactionType === 'Payment') return flow.action.includes('Issue') || flow.action.includes('Redeem') ? 'issuance' : 'transfer';
  if (flow.transactionType === 'Clawback' || flow.transactionType.startsWith('MPTokenIssuance')) return 'issuance';
  if (flow.transactionType.includes('Offer') || flow.transactionType.startsWith('AMM')) return 'dex';
  if (flow.transactionType === 'TrustSet' || flow.transactionType === 'MPTokenAuthorize') return 'trust';
  return 'program';
}

function formatPair(pair) {
  if (!pair?.length) return null;
  return pair.map((side) => `${formatNumber(side.value, { compact: true })} ${side.ticker}`).join(' for ');
}

// Single-series bar sparkline: live matches per minute over the trailing window.
function CadenceSparkline({ flows, now }) {
  const buckets = useMemo(() => flowCadence(flows, { minutes: CADENCE_MINUTES, now }), [flows, now]);
  const max = Math.max(1, ...buckets.map((bucket) => bucket.count));
  const total = buckets.reduce((sum, bucket) => sum + bucket.count, 0);
  const width = 100;
  const height = 22;
  const gap = 0.6;
  const barWidth = width / CADENCE_MINUTES - gap;

  return (
    <div className="cadence" aria-label={`${total} live matches in the last ${CADENCE_MINUTES} minutes`}>
      <div className="cadence-copy">
        <span>Last {CADENCE_MINUTES} min</span>
        <strong>{total}</strong>
      </div>
      <svg viewBox={`0 0 ${width} ${height}`} preserveAspectRatio="none" role="img" aria-hidden="true" className="cadence-chart">
        {buckets.map((bucket, index) => {
          const barHeight = bucket.count ? Math.max(2, (bucket.count / max) * (height - 2)) : 1;
          return (
            <rect
              key={bucket.minuteStart}
              x={index * (barWidth + gap)}
              y={height - barHeight}
              width={barWidth}
              height={barHeight}
              rx={0.8}
              className={bucket.count ? 'cadence-bar' : 'cadence-bar empty'}
            >
              <title>{`${bucket.count} match${bucket.count === 1 ? '' : 'es'} · ${new Date(bucket.minuteStart).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}`}</title>
            </rect>
          );
        })}
      </svg>
    </div>
  );
}

export function FlowFeed({ flows, status, ledgerIndex, endpoint, onSelect }) {
  const [filter, setFilter] = useState('all');
  const [sourceFilter, setSourceFilter] = useState('all');
  const [paused, setPaused] = useState(false);
  const [frozenFlows, setFrozenFlows] = useState([]);
  const [now, setNow] = useState(() => Date.now());

  useEffect(() => {
    if (!paused) setFrozenFlows(flows);
  }, [flows, paused]);

  useEffect(() => {
    const timer = setInterval(() => setNow(Date.now()), 10_000);
    return () => clearInterval(timer);
  }, []);

  const displayed = paused ? frozenFlows : flows;
  const visible = useMemo(() => displayed.filter((flow) => {
    if (sourceFilter !== 'all' && flow.source !== sourceFilter) return false;
    if (filter === 'all') return true;
    if (filter === 'rwa' || filter === 'stablecoin') return flow.category === filter;
    return flowGroup(flow) === filter;
  }), [displayed, filter, sourceFilter]);
  const liveCount = flows.filter((flow) => flow.source === 'live').length;
  const historyCount = flows.filter((flow) => flow.source === 'history').length;
  const frozenIds = useMemo(() => new Set(frozenFlows.map((flow) => flow.id)), [frozenFlows]);
  const pendingCount = paused ? flows.filter((flow) => !frozenIds.has(flow.id)).length : 0;

  const togglePause = () => {
    if (!paused) setFrozenFlows(flows);
    setPaused((value) => !value);
  };

  return (
    <section className="flow-panel panel" aria-labelledby="flow-title">
      <div className="panel-heading flow-heading">
        <div>
          <span className="eyebrow">Validated transactions</span>
          <h2 id="flow-title">Asset flow</h2>
        </div>
        <button className={`pause-button ${paused ? 'active' : ''}`} onClick={togglePause} aria-pressed={paused}>
          {paused ? `Resume${pendingCount ? ` · ${pendingCount} new` : ''}` : 'Pause'}
        </button>
      </div>

      <div className="stream-health">
        <span className={`connection-dot connection-${status}`} />
        <span>{status === 'live' ? 'Mainnet stream live' : status}</span>
        <span className="ledger-number">L {ledgerIndex ? Number(ledgerIndex).toLocaleString() : '—'}</span>
      </div>
      <CadenceSparkline flows={flows} now={now} />
      <p className="context-note">{liveCount} live match{liveCount === 1 ? '' : 'es'} this session · {historyCount} recent historical match{historyCount === 1 ? '' : 'es'}.</p>
      <span className="sr-only" role="status" aria-live="polite" aria-atomic="true">
        {paused ? `Flow paused. ${pendingCount} new matching transactions waiting.` : status === 'live' ? 'XRPL transaction stream live.' : `XRPL stream ${status}.`}
      </span>

      <div className="flow-source-row" role="group" aria-label="Transaction source">
        {sourceFilters.map(([value, label]) => (
          <button key={value} className={sourceFilter === value ? 'active' : ''} onClick={() => setSourceFilter(value)} aria-pressed={sourceFilter === value}>{label}</button>
        ))}
      </div>

      <div className="flow-filter-row" role="group" aria-label="Transaction filter">
        {flowFilters.map(([value, label]) => (
          <button key={value} className={filter === value ? 'active' : ''} onClick={() => setFilter(value)} aria-pressed={filter === value}>{label}</button>
        ))}
      </div>

      <div className="flow-list" aria-busy={status === 'connecting'}>
        {visible.map((flow) => {
          const relative = formatRelativeTime(flow.date, now);
          return (
            <article className={`flow-card flow-${flowGroup(flow)} ${flow.source === 'live' ? 'flow-enter' : ''}`} key={flow.id}>
              <div className="flow-card-head">
                <button className="flow-program" onClick={() => onSelect(flow.programId)}>{flow.programName}</button>
                <span className="flow-relative" title={formatAbsoluteTime(flow.date)}>{relative || formatLedgerTime(flow.date)}</span>
              </div>
              <div className="flow-title-row">
                <strong>{flow.action}</strong>
                <span className={`source-chip source-${flow.source}`}>{flow.source === 'live' ? 'LIVE' : 'HISTORY'}</span>
              </div>
              {flow.amount !== null && flow.amount !== undefined && (
                <div className="flow-amount">{formatNumber(flow.amount)} <span>{flow.ticker}</span></div>
              )}
              {flow.amountRole && <small className="amount-role">{flow.amountRole}</small>}
              {flow.pair && <div className="flow-pair">Order: {formatPair(flow.pair)}</div>}
              <div className="flow-route" aria-label={`From ${flow.account || 'unpublished account'} to ${flow.destination || 'unpublished destination'}`}>
                <span title={flow.account || 'Account not published'}>{shortAddress(flow.account)}</span>
                <i>→</i>
                <span title={flow.destination || 'Destination not published'}>{shortAddress(flow.destination)}</span>
              </div>
              <div className="flow-meta">
                <span title={formatAbsoluteTime(flow.date)}>{formatLedgerTime(flow.date)}</span>
                <span>{flow.transactionType}</span>
                {flow.ledgerIndex && <span>#{Number(flow.ledgerIndex).toLocaleString()}</span>}
                {flow.hash && <a href={flow.href} target="_blank" rel="noreferrer" aria-label="Open transaction in XRPL explorer">↗</a>}
              </div>
            </article>
          );
        })}
        {!visible.length && (
          <div className="empty-state flow-empty">
            <span className="listening-pulse" />
            <strong>{status === 'live' ? 'No matches in this view yet' : 'Connecting to XRPL mainnet'}</strong>
            <p>{paused ? 'Resume the stream or change filters to see incoming matches.' : 'Only successful transactions tied to a tracked identity appear here.'}</p>
          </div>
        )}
      </div>
      <div className="endpoint-note" title={endpoint || ''}>{endpoint ? endpoint.replace('wss://', '') : 'Selecting a public endpoint'}</div>
    </section>
  );
}
